import { useCallback, useMemo } from 'react';
import { useActivePackages } from './useActivePackages';
import { PLATFORM_ACCESS_UUID } from './useDowngradeOptions';

const WEEKS_OPTIONS = [4, 12, 24, 52];
const MAX_WEEKS = Math.max(...WEEKS_OPTIONS);

export interface UpgradePackageOption {
  value: string;
  label: string;
  sessionsPerWeek: number;
}

export interface UseUpgradeOptionsParams {
  /** UUID of the plan the student is currently enrolled to. */
  student_plan_uuid: string;
  /** UUID of the plan currently selected in the dropdown. */
  selectedPackageUUID: string;
  no_of_weeks_originally_enrolled_to: number;
}

// Upgrade targets: plans at or above the current tier; Platform Access (base tier) gets all.
export const buildUpgradePlanOptions = ({
  allPackages,
  currentTier,
  student_plan_uuid,
  no_of_weeks_originally_enrolled_to
}: {
  allPackages: UpgradePackageOption[];
  currentTier: number;
  student_plan_uuid: string;
  no_of_weeks_originally_enrolled_to: number;
}): UpgradePackageOption[] => {
  const atMaxWeeks = Number(no_of_weeks_originally_enrolled_to || 0) >= MAX_WEEKS;
  const currentIsPlatformAccess = student_plan_uuid === PLATFORM_ACCESS_UUID;
  return allPackages.filter((pkg) => {
    if (pkg.value === PLATFORM_ACCESS_UUID) return false;
    if (atMaxWeeks && pkg.value === student_plan_uuid) return false;
    // By UUID, not tier: the catalog carries Platform Access at spw 1 (PROD-8851).
    if (currentIsPlatformAccess) return true;
    if (currentTier === -1) return false;
    return pkg.sessionsPerWeek >= currentTier;
  });
};

// Allowed billing-cadence (weeks) options: switching to a different plan offers the full
// cadence list; staying on the same plan must add weeks (strictly greater than the current
// enrollment), falling back to the full list if none are greater.
export const buildUpgradeWeeksOptions = ({
  selectedPackageUUID,
  student_plan_uuid,
  no_of_weeks_originally_enrolled_to
}: {
  selectedPackageUUID: string;
  student_plan_uuid: string;
  no_of_weeks_originally_enrolled_to: number;
}): number[] => {
  if (!selectedPackageUUID || selectedPackageUUID !== student_plan_uuid) {
    return WEEKS_OPTIONS;
  }
  const currentWeeks = Number(no_of_weeks_originally_enrolled_to || 0);
  const filtered = WEEKS_OPTIONS.filter((w) => w > currentWeeks);
  return filtered.length ? filtered : WEEKS_OPTIONS;
};

/**
 * Single source of truth for the membership-upgrade option lists in UpgradeMembership.
 * Mirrors useDowngradeOptions: plan tier is derived from the package's `sessions_per_week`
 * (from the active_packages API) rather than a hardcoded UUID list, so newer plans —
 * including the Consistency 1/2/3-per-week tiers and the hidden 8-21 tiers — appear
 * automatically. Upgrade and downgrade now share the same catalog and can never drift apart.
 */
export const useUpgradeOptions = ({
  student_plan_uuid,
  selectedPackageUUID,
  no_of_weeks_originally_enrolled_to
}: UseUpgradeOptionsParams) => {
  const { packages, error } = useActivePackages();

  const allPackages = useMemo<UpgradePackageOption[]>(
    () =>
      packages.map((p) => ({
        value: p.uuid,
        label: p.name,
        sessionsPerWeek: p.sessions_per_week
      })),
    [packages]
  );

  const getPlanTier = useCallback(
    (uuid: string) => {
      const pkg = allPackages.find((p) => p.value === uuid);
      return pkg ? pkg.sessionsPerWeek : -1;
    },
    [allPackages]
  );

  const currentTier = getPlanTier(student_plan_uuid);
  const selectedTier = getPlanTier(selectedPackageUUID);

  const planOptions = useMemo(
    () =>
      buildUpgradePlanOptions({
        allPackages,
        currentTier,
        student_plan_uuid,
        no_of_weeks_originally_enrolled_to
      }),
    [allPackages, currentTier, student_plan_uuid, no_of_weeks_originally_enrolled_to]
  );

  const weeksOptions = useMemo(
    () =>
      buildUpgradeWeeksOptions({
        selectedPackageUUID,
        student_plan_uuid,
        no_of_weeks_originally_enrolled_to
      }),
    [selectedPackageUUID, student_plan_uuid, no_of_weeks_originally_enrolled_to]
  );

  const getPackageName = useCallback(
    (uuid: string) => allPackages.find((p) => p.value === uuid)?.label || uuid,
    [allPackages]
  );

  return {
    allPackages,
    planOptions,
    weeksOptions,
    currentTier,
    selectedTier,
    getPackageName,
    error
  };
};
